import { z } from "zod";
import { EexStatsQuerySchema } from "@chro/shared";

const ViewSchema = z.object({
  period: z.enum(["ytd", "quarter", "month", "last-year", "custom"]),
  range: z.object({ from: z.string(), to: z.string() }),
  focus: z.object({ book: z.string().optional(), trader: z.string().optional(), commodity: z.enum(["power", "gas"]).optional(), month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional() }),
  dimension: z.enum(["book", "trader", "cross"]), lens: z.enum(["activity", "performance"]),
  measure: z.enum(["energyMwh", "powerMwh", "gasMwh", "executions", "pnlEur", "mtmEur", "costsEur"]),
  rhythm: z.enum(["volume", "trades"]), origin: z.enum(["all", "cleared", "bilateral"]),
});
export type StatsView = z.infer<typeof ViewSchema>;
export type StatsPeriod = StatsView["period"];

export function statsRangeFor(period: StatsPeriod, today: string): { from: string; to: string } {
  const year = Number(today.slice(0, 4));
  if (period === "last-year") return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
  const quarterMonth = String(Math.floor((Number(today.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, "0");
  const from = period === "month" ? `${today.slice(0, 7)}-01` : period === "quarter" ? `${year}-${quarterMonth}-01` : `${year}-01-01`;
  return { from, to: today };
}

export function restoreStatsView(raw: string | null, today: string): StatsView {
  const defaults: StatsView = { period: "ytd", range: statsRangeFor("ytd", today), focus: {}, dimension: "trader", lens: "activity", measure: "energyMwh", rhythm: "volume", origin: "all" };
  try {
    const parsed = ViewSchema.safeParse(JSON.parse(raw ?? "null"));
    if (!parsed.success) return defaults;
    const view = parsed.data;
    if (view.period !== "custom") view.range = statsRangeFor(view.period, today);
    if (!EexStatsQuerySchema.safeParse({ ...view.range, book: view.focus.book, trader: view.focus.trader, commodity: view.focus.commodity ?? "all", origin: view.origin }).success) return defaults;
    if (view.focus.month && (view.focus.month < view.range.from.slice(0, 7) || view.focus.month > view.range.to.slice(0, 7))) delete view.focus.month;
    return view;
  } catch { return defaults; }
}

export function readStatsView(key: string, today: string): StatsView {
  try { return restoreStatsView(window.sessionStorage.getItem(key), today); }
  catch { return restoreStatsView(null, today); }
}
